import type {EditorView, KeyBinding} from '@codemirror/view'
import type {ShowSuggestionsEvent, Suggestion, Suggestions} from '@github-ui/inline-autocomplete/types'
import {getSuggestionKey, getSuggestionValue, isNullSuggestion} from '@github-ui/inline-autocomplete/utils'
import {type Dispatch, type SetStateAction, useCallback, useEffect, useMemo, useRef, useState} from 'react'

import type {AutocompleteState, HybridEditorTelemetry, SuggestionKind} from '../telemetry'

type UseAutocompleteStateProps = {
  suggestions: Suggestions | null
  setSuggestionEvent: (event: ShowSuggestionsEvent | null) => void
  telemetry?: HybridEditorTelemetry
}

type TriggerCharCoords = {top: number; left: number; height: number}

type UseAutocompleteStateResult = {
  activeSuggestionKey: string | undefined
  autocompleteKeymap: KeyBinding[]
  autocompleteVisible: boolean
  activeSuggestion: Suggestion | undefined
  insertAutocompleteSelection: (editor: EditorView, suggestion: Suggestion) => void
  setActiveSuggestionKey: Dispatch<SetStateAction<string | undefined>>
  setAutocompleteVisible: Dispatch<SetStateAction<boolean>>
  setTriggerCharCoords: Dispatch<SetStateAction<TriggerCharCoords>>
  showEventRef: React.MutableRefObject<ShowSuggestionsEvent | null>
  triggerCharCoords: TriggerCharCoords
}

export const useAutocompleteState = ({
  suggestions,
  setSuggestionEvent,
  telemetry,
}: UseAutocompleteStateProps): UseAutocompleteStateResult => {
  /** Stores the query that caused the current suggestion list to appear. */
  const showEventRef = useRef<ShowSuggestionsEvent | null>(null)
  const [autocompleteVisible, setAutocompleteVisible] = useState(false)
  const [triggerCharCoords, setTriggerCharCoords] = useState<TriggerCharCoords>({
    top: 0,
    left: 0,
    height: 0,
  })
  const [activeSuggestionKey, setActiveSuggestionKey] = useState<string>()
  const activeSuggestion = Array.isArray(suggestions)
    ? suggestions.find(s => getSuggestionKey(s) === activeSuggestionKey) ?? suggestions[0]
    : undefined

  if (activeSuggestionKey && !autocompleteVisible) {
    setActiveSuggestionKey(undefined)
  }

  const insertAutocompleteSelection = useCallback(
    (editor: EditorView, suggestion: Suggestion) => {
      if (!suggestion) return

      const isMultistep = isNullSuggestion(suggestion)
      const suggestionValue = getSuggestionValue(suggestion) ?? ''
      const suggestionKind: SuggestionKind = typeof suggestion === 'string' ? 'string' : isMultistep ? 'null' : 'object'

      if (!showEventRef?.current) return
      const caretLocation = editor.state.selection.main.anchor ?? 0
      const {trigger} = showEventRef.current

      const keepTriggerChar = isMultistep || (trigger.keepTriggerCharOnCommit ?? true)
      const maybeTriggerChar = keepTriggerChar ? trigger.triggerChar : ''

      const insertSpace = !isMultistep && (trigger.insertSpaceOnCommit ?? true)
      const maybeSpace = insertSpace ? ' ' : ''

      const editorInsertion = `${maybeTriggerChar}${suggestionValue}${maybeSpace}`
      const docString = editor.state.doc.toString()

      // It's possible we're actually shortening the document length with the transaction (which happens when the
      // user types 3+ characters before selecting an emoji to insert), so we have to place the caret right after
      // the last instance of the trigger character to avoid out-of-range issues.
      const triggerCharPosition = docString.lastIndexOf(trigger.triggerChar, caretLocation)

      editor.dispatch({
        changes: [
          {
            from: triggerCharPosition,
            to: caretLocation,
            insert: editorInsertion,
          },
        ],
        selection: {anchor: triggerCharPosition + editorInsertion.length},
        userEvent: 'input.complete',
        scrollIntoView: true,
      })
      setAutocompleteVisible(false)
      setSuggestionEvent(null)

      telemetry?.trackAutocompleteSelected({
        triggerChar: trigger.triggerChar,
        insertedLength: editorInsertion.length,
        multiStep: isMultistep,
        suggestionKind,
      })
    },
    [showEventRef, setAutocompleteVisible, setSuggestionEvent, telemetry],
  )

  const navigate = useCallback(
    (direction: 'up' | 'down' | 'start' | 'end') => {
      // Suggestions can be visible but not navigable when the the loading state is shown
      // The Array.isArray check is fully redundant but satisfies the typechecker
      if (!activeSuggestion || !Array.isArray(suggestions)) return

      const setActiveSuggestion = (suggestion: Suggestion | undefined) => {
        setActiveSuggestionKey(suggestion ? getSuggestionKey(suggestion) : undefined)
      }

      switch (direction) {
        case 'up':
          setActiveSuggestion(suggestions[suggestions.indexOf(activeSuggestion) - 1] ?? suggestions.at(-1))
          break
        case 'down':
          setActiveSuggestion(suggestions[suggestions.indexOf(activeSuggestion) + 1] ?? suggestions[0])
          break
        case 'start':
          setActiveSuggestion(suggestions[0])
          break
        case 'end':
          setActiveSuggestion(suggestions.at(-1))
          break
      }
    },
    [activeSuggestion, suggestions, setActiveSuggestionKey],
  )

  const onCommit = useCallback(
    (editorView: EditorView) => {
      if (!activeSuggestion || (typeof activeSuggestion !== 'string' && activeSuggestion.value === null)) return false
      if (!showEventRef?.current) return false

      insertAutocompleteSelection(editorView, activeSuggestion)
      return true
    },
    [activeSuggestion, insertAutocompleteSelection, showEventRef],
  )

  const autocompleteKeymap: KeyBinding[] = useMemo(
    () => [
      {
        key: 'Enter',
        run: onCommit,
        preventDefault: true,
        stopPropagation: true,
      },
      {
        key: 'ArrowUp',
        run: () => {
          navigate('up')
          return true
        },
      },
      {
        key: 'ArrowDown',
        run: () => {
          navigate('down')
          return true
        },
      },
      {
        key: 'Home',
        run: () => {
          navigate('start')
          return true
        },
        preventDefault: true,
        stopPropagation: true,
      },
      {
        key: 'End',
        run: () => {
          navigate('end')
          return true
        },
        preventDefault: true,
        stopPropagation: true,
      },
      {
        key: 'Tab',
        run: onCommit,
        preventDefault: true,
        stopPropagation: true,
      },
    ],
    [onCommit, navigate],
  )

  const lastTrackedQueryRef = useRef<string | null>(null)
  const lastTrackedStateRef = useRef<AutocompleteState | null>(null)

  useEffect(() => {
    if (!telemetry) {
      return
    }

    if (!autocompleteVisible || !showEventRef.current) {
      if (!autocompleteVisible) {
        lastTrackedQueryRef.current = null
        lastTrackedStateRef.current = null
      }
      return
    }

    const {query, trigger} = showEventRef.current
    let suggestionState: AutocompleteState = 'empty'
    let suggestionCount: number | undefined

    if (suggestions === 'loading') {
      suggestionState = 'loading'
    } else if (Array.isArray(suggestions)) {
      suggestionCount = suggestions.length
      suggestionState = suggestionCount > 0 ? 'results' : 'empty'
    }

    const shouldTrack = lastTrackedQueryRef.current !== query || lastTrackedStateRef.current !== suggestionState

    if (shouldTrack) {
      telemetry.trackAutocompleteShown({
        triggerChar: trigger.triggerChar,
        queryLength: query.length,
        suggestionState,
        suggestionCount,
      })
      lastTrackedQueryRef.current = query
      lastTrackedStateRef.current = suggestionState
    }
  }, [autocompleteVisible, suggestions, telemetry])

  return {
    activeSuggestionKey,
    autocompleteKeymap,
    autocompleteVisible,
    activeSuggestion,
    insertAutocompleteSelection,
    setActiveSuggestionKey,
    setAutocompleteVisible,
    setTriggerCharCoords,
    showEventRef,
    triggerCharCoords,
  }
}
